import { useCallback, useState } from 'react'
import { useListaDeGmuds } from '../../../api/queries/gmuds'
import { temPermissao, useSessao } from '../../../auth/contexto'
import { BotaoLink } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto, EstadoVazio } from '../../../components/Estados'
import { IconeNovo } from '../../../components/Icones'
import { IconeQuadro } from '../../../components/IconesDeNavegacao'
import { TERMOS, TEXTOS } from '../../../lib/glossario'
import { recorteDoQuadro, STATUS_EM_FLUXO, STATUS_FORA_DO_FLUXO } from './colunasDoQuadro'
import { Quadro } from './Quadro'
import { RodapeForaDoFluxo } from './RodapeForaDoFluxo'
import { QUADRO } from './textosDoQuadro'

/**
 * O quadro: onde cada solicitação parou, e de quem se está esperando.
 *
 * **São duas consultas, e as duas com `origem=V2`.** A primeira traz o que está em alguma das sete
 * etapas — é o que vira coluna; a segunda traz os três terminais, que viram rodapé com contagem. O
 * recorte de procedência não é preferência de tela: as 1.027 solicitações trazidas do sistema
 * anterior não percorreram este processo (colocá-las numa coluna diria que percorreram) e não têm
 * linha de saída na matriz — seriam cartões imóveis para sempre.
 *
 * **O teto de 100 é do servidor** (`GmudConsultaService.TAMANHO_MAXIMO_PAGINA`) e não há como pedir
 * mais. Em vez de paginar um quadro — o que hoje seria construir o defeito e esperar, já que o
 * recorte inteiro cabe numa página —, a tela diz quantas está mostrando de quantas e manda para a
 * lista, que pagina de verdade.
 *
 * **No celular o quadro não é oferecido.** Sete colunas em 360px não existem, e um acordeão por
 * coluna seria uma terceira interface a manter e a ensinar. A troca é por CSS e não por
 * `matchMedia`: medir largura em JavaScript faz a página montar com a forma errada e trocar depois.
 *
 * @returns o quadro
 */
export function PaginaQuadroGmuds() {
  const [anuncio, setAnuncio] = useState('')
  // Estável entre desenhos: os efeitos do cartão que anunciam o desfecho dependem desta função.
  const aoAnunciar = useCallback((texto: string) => setAnuncio(texto), [])
  const emFluxo = useListaDeGmuds(recorteDoQuadro(STATUS_EM_FLUXO))
  const foraDoFluxo = useListaDeGmuds(recorteDoQuadro(STATUS_FORA_DO_FLUXO))
  const { usuario } = useSessao()
  const podeCriar = temPermissao(usuario, 'GMUD_CRIAR')

  const abrirSolicitacao = podeCriar ? (
    <BotaoLink to="/gmuds/novo" aparencia="primario">
      <IconeNovo className="h-4 w-4" />
      {TEXTOS.abrirSolicitacao}
    </BotaoLink>
  ) : undefined

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="titulo-da-pagina">{QUADRO.titulo}</h1>
          <p className="mt-1 max-w-prose text-sm text-slate-600">{QUADRO.subtitulo}</p>
        </div>
        {abrirSolicitacao}
      </header>

      <div className="lg:hidden">
        <EstadoVazio
          icone={<IconeQuadro className="h-9 w-9" />}
          titulo={QUADRO.soNoComputador.titulo}
          ajuda={QUADRO.soNoComputador.ajuda}
          acao={
            <BotaoLink to="/gmuds">{TERMOS.gmud.rotuloPlural}</BotaoLink>
          }
        />
      </div>

      <div className="hidden space-y-4 lg:block">
        {emFluxo.isPending && (
          <div className="cartao px-4 py-5">
            <Esqueleto linhas={8} />
          </div>
        )}
        {emFluxo.isError && <AvisoDeErro erro={emFluxo.error} titulo="Não foi possível carregar o quadro" />}

        {emFluxo.data && emFluxo.data.conteudo.length === 0 && (
          <EstadoVazio
            icone={<IconeQuadro className="h-9 w-9" />}
            titulo={QUADRO.vazio.titulo}
            ajuda={QUADRO.vazio.ajuda}
            acao={abrirSolicitacao}
          />
        )}

        {emFluxo.data && emFluxo.data.conteudo.length > 0 && (
          <>
            {emFluxo.data.totalElementos > emFluxo.data.conteudo.length && (
              <p className="text-sm text-slate-600">
                {QUADRO.truncado(emFluxo.data.conteudo.length, emFluxo.data.totalElementos)}
              </p>
            )}
            <Quadro itens={emFluxo.data.conteudo} aoAnunciar={aoAnunciar} />
          </>
        )}

        {foraDoFluxo.data && <RodapeForaDoFluxo pagina={foraDoFluxo.data} />}
      </div>

      {/* A região que anuncia o desfecho de cada transição. Ela fica FORA do bloco escondido no
          celular: `display:none` some da árvore de acessibilidade, e uma região que some não
          anuncia nada. */}
      <p role="status" aria-live="polite" className="sr-only">
        {anuncio}
      </p>
    </div>
  )
}
